import {
  fadeScale,
  isExpired,
  nudge as nudgeState,
  parseSleep,
  SLEEP_IDLE_CANCEL_MS,
  type SleepState,
  sleepFromDuration,
} from '../domain/sleep';
import type { StoragePort } from '../ports/storage';
import type { AppState } from './app';
import type { Store } from './index';

/**
 * Krmilnik časovnika spanja (P2 §5).
 *
 * Stanje (`{ endsAt }`) živi v `sessionStorage` prek `StoragePort` (ključ
 * `sleep`) — **ne sme preživeti zaprtja zavihka** (T2 §3).
 *
 * Utrip (1 s) teče le, dokler je časovnik nastavljen. Ob vsakem tiku:
 *   1. preveri samodejni preklic po 5 min brez predvajanja (§5.6)
 *   2. preveri potek → ustavi in počisti
 *   3. postavi faktor postopnega utišanja zadnjih 30 s (§5.8)
 *   4. objavi **svež** `{ endsAt }`, da odštevanje in značka znova izračunata
 *      preostanek — vsebina je enaka, referenca nova, in prav to je znak za
 *      izris (store primerja po referenci)
 *
 * ⚠️ **Merilo je ura, ne tiki.** `endsAt` je absoluten; preostanek se računa iz
 * trenutne ure. V ozadju brskalnik interval upočasni, a ob vrnitvi v ospredje
 * je izračun takoj pravilen. Zaustavitev zvoka **med** ozadjem ni zajamčena —
 * to je omejitev spletne plasti (glej D1, faza 5).
 */

const TICK_MS = 1000;

export interface SleepDeps {
  store: Store<AppState>;
  storage: StoragePort;
  now?: () => number;
  /** Časovnik je potekel — ustavi predvajanje. */
  onExpire(): void;
  /**
   * Faktor izhodne glasnosti (1 = polna, 0 = tiho) za postopno utišanje.
   * Množi se z uporabnikovo glasnostjo; klicno mesto jo pomnoži in postavi.
   */
  setOutputScale(scale: number): void;
}

export interface SleepController {
  /** Obnovi časovnik iz shrambe (potekli se zavržejo). */
  init(): void;
  /** Nastavi časovnik na trajanje v ms. */
  start(durationMs: number): void;
  cancel(): void;
  /** Podaljša ali skrajša za `deltaMs` (±5 min); brez učinka, kadar ni aktiven. */
  nudge(deltaMs: number): void;
  /** Opazuje predvajanje za samodejni preklic. */
  sync(state: AppState): void;
  dispose(): void;
}

export function createSleepController(deps: SleepDeps): SleepController {
  const { store, storage } = deps;
  const now = deps.now ?? (() => Date.now());

  let timer: ReturnType<typeof setInterval> | null = null;
  let lastScale = 1;
  let playing = false;
  /** Kdaj se je nazadnje nehalo predvajati; `null`, dokler nekaj igra. */
  let idleSince: number | null = null;

  function stopTimer(): void {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  }

  function applyScale(scale: number): void {
    if (scale === lastScale) return;
    lastScale = scale;
    deps.setOutputScale(scale);
  }

  function write(state: SleepState | null): void {
    store.set({ sleep: state });
    if (state === null) {
      void storage.clear('sleep');
      stopTimer();
      return;
    }
    void storage.save('sleep', state);
    if (timer === null) timer = setInterval(tick, TICK_MS);
  }

  function finish(): void {
    // Ustavi zvok, nato glasnost vrni na polno — da naslednje predvajanje ne
    // ostane utišano od pojemanja.
    deps.onExpire();
    applyScale(1);
    write(null);
  }

  function tick(): void {
    const state = store.get().sleep;
    if (state === null) {
      stopTimer();
      return;
    }
    const at = now();

    if (idleSince !== null && at - idleSince >= SLEEP_IDLE_CANCEL_MS) {
      applyScale(1);
      write(null);
      return;
    }

    if (isExpired(state, at)) {
      finish();
      return;
    }

    applyScale(fadeScale(state, at));
    // Svež objekt je znak za izris odštevanja in značke (glej opis zgoraj).
    store.set({ sleep: { endsAt: state.endsAt } });
  }

  return {
    init() {
      // Stanje živi v sessionStorage prek StoragePort; store je ob zagonu prazen.
      void storage.load<unknown>('sleep').then((raw) => {
        const state = parseSleep(raw, now());
        if (state === null) {
          void storage.clear('sleep');
          return;
        }
        idleSince = playing ? null : now();
        write(state);
      });
    },

    start(durationMs) {
      lastScale = 1;
      idleSince = playing ? null : now();
      write(sleepFromDuration(durationMs, now()));
    },

    cancel() {
      applyScale(1);
      write(null);
    },

    nudge(deltaMs) {
      const state = store.get().sleep;
      if (state === null) return;
      write(nudgeState(state, deltaMs, now()));
    },

    sync(state) {
      const next = state.playback === 'playing';
      if (next === playing) return;
      playing = next;
      if (store.get().sleep === null) return;
      idleSince = playing ? null : now();
    },

    dispose() {
      stopTimer();
    },
  };
}
